import { useMemo, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axiosClient from "../axiosClient";

function PaymentForm({
  paymentIntentId,
  programOption,
  amountDisplay,
  baseDisplay,
  feeDisplay,
  studentName,
  onMissingStudentName,
  onSuccess,
  onError,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || isSubmitting) {
      return;
    }

    const trimmedName = (studentName || "").trim();
    if (!trimmedName) {
      setErrorMessage("Please enter the student name above before paying.");
      if (onMissingStudentName) {
        onMissingStudentName();
      }
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await axiosClient.post("/middleSchoolPrepRegistrations/prepare", {
        paymentIntentId,
        programOption,
        studentName: trimmedName,
      });

      const returnUrl = `${window.location.origin}${window.location.pathname}?paid=1`;

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(error.message || "Payment failed. Please try again.");
        setIsSubmitting(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        try {
          await axiosClient.post("/middleSchoolPrepRegistrations/confirm", {
            paymentIntentId: paymentIntent.id || paymentIntentId,
            programOption,
            studentName: trimmedName,
          });
        } catch (confirmError) {
          const confirmMessage =
            confirmError?.response?.data?.message ||
            "Payment succeeded but confirmation failed. Please contact the school.";
          setErrorMessage(confirmMessage);
          setIsSubmitting(false);
          return;
        }

        onSuccess();
        return;
      }

      setErrorMessage("Payment is still processing. Please wait a moment.");
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "We could not complete payment. Please try again.";
      setErrorMessage(message);
      if (onError) {
        onError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="middle-school-prep-stripe-form" onSubmit={handleSubmit}>
      <div className="middle-school-prep-stripe-summary">
        <p>
          Tuition: <strong>{baseDisplay}</strong>
        </p>
        <p>
          Stripe processing fees: <strong>{feeDisplay}</strong>
        </p>
        <p className="middle-school-prep-stripe-total">
          Total: <strong>{amountDisplay}</strong>
        </p>
      </div>

      <PaymentElement
        options={{
          layout: "tabs",
        }}
      />

      {errorMessage && (
        <span className="registration-form-error">{errorMessage}</span>
      )}

      <div className="registration-form-actions">
        <input
          type="submit"
          value={
            isSubmitting
              ? "Processing..."
              : `Pay ${amountDisplay} with Credit Card`
          }
          disabled={!stripe || !elements || isSubmitting}
        />
      </div>
    </form>
  );
}

const MiddleSchoolPrepStripeCheckout = ({
  clientSecret,
  publishableKey,
  paymentIntentId,
  programOption,
  amountDisplay,
  baseDisplay,
  feeDisplay,
  studentName,
  onMissingStudentName,
  onSuccess,
  onError,
}) => {
  const stripePromise = useMemo(
    () => loadStripe(publishableKey),
    [publishableKey]
  );

  if (!clientSecret || !publishableKey) {
    return null;
  }

  return (
    <div className="middle-school-prep-stripe-embed">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#1565c0",
              borderRadius: "8px",
            },
          },
        }}
      >
        <PaymentForm
          paymentIntentId={paymentIntentId}
          programOption={programOption}
          amountDisplay={amountDisplay}
          baseDisplay={baseDisplay}
          feeDisplay={feeDisplay}
          studentName={studentName}
          onMissingStudentName={onMissingStudentName}
          onSuccess={onSuccess}
          onError={onError}
        />
      </Elements>
    </div>
  );
};

export default MiddleSchoolPrepStripeCheckout;
